import { zodResolver } from '@hookform/resolvers/zod';
import { FormFloatField, type FormFloatFieldProps } from '@mdvip/shared/components/blocks/FormFloatField';
import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Alert, AlertDescription } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { formatZip } from '@mdvip/shared/lib/us-zip';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { MemberProfile } from '@/hooks/profile/profileApi';
import { useUpdateMyProfile } from '@/hooks/profile/useUpdateMyProfile';
import { formatPhone } from '@/lib/profile/profile.format';
import { createProfileSchema } from '@/lib/profile/profile.schema';
import type { OriginalAddressLines, ProfileFormValues, ProfileValidationIssue } from '@/lib/profile/profile.types';
import { isSalutation } from '@/lib/profile/profile.types';
import { isUsStateCode, US_STATE_CODES } from '@/lib/profile/usStates';

interface EditProfileDialogProps {
  open: boolean;
  member: MemberProfile;
  onClose: () => void;
}

/**
 * The single whole-record Edit dialog `ProfileDetailsCard`'s Edit button opens — every editable
 * field across all 4 read-view sections in one form, matching legacy's single-modal behavior.
 * `{open && <Content />}` unmounts the form entirely on close, so reopening always re-seeds from
 * the caller's current `member` snapshot rather than resuming stale in-progress edits.
 */
export function EditProfileDialog({ open, member, onClose }: EditProfileDialogProps) {
  // Lifted out of EditProfileDialogContent so the close (X) button — owned by the sibling
  // ResponsiveModal — can be hidden while a save is in flight. Hiding it alone doesn't block
  // Escape or an outside click (Base UI's Dialog still fires `onOpenChange(false)` for both), so
  // the guard in `onOpenChange` below has to exist too.
  const [isSaving, setIsSaving] = useState(false);
  // Routes every close through here so a successful save resets `isSaving` in the same event that
  // closes the dialog — the `isPending` effect below can't do it: `onClose` unmounts the content
  // in the same React batch that flips `isPending` false, so the effect never re-renders to see it,
  // and `isSaving` would stay `true` forever otherwise.
  const handleClose = () => {
    setIsSaving(false);
    onClose();
  };
  return (
    <ResponsiveModal
      open={open}
      onOpenChange={(next) => !next && !isSaving && handleClose()}
      className="sm:max-w-2xl"
      showCloseButton={!isSaving}
    >
      {open && <EditProfileDialogContent member={member} onClose={handleClose} onSavingChange={setIsSaving} />}
    </ResponsiveModal>
  );
}

/**
 * The design's street field is one plain single-line input, so this joins `line1`/`line2` with a
 * comma for the *displayed* seed value — deliberately not a newline, since an `<input>` can't
 * render one (verified live: an embedded `\n` renders as both lines jammed together unreadably).
 * Only the display pays this cost — `buildUpdatePayload` sends the real, separate `line1`/`line2`
 * pair (`OriginalAddressLines`) for any address the member doesn't actually edit.
 */
function joinAddressLines(line1: string | null, line2: string | null): string {
  return [line1, line2].filter((line): line is string => Boolean(line?.trim())).join(', ');
}

/**
 * Maps a backend validation path to the one flattened form field that actually renders it.
 * `updateMyMemberInfo`'s input is two-line (`mailingLine1`/`mailingLine2`/`otherLine1`/
 * `otherLine2`), but this dialog's street field is one flattened `mailingStreet`/`otherStreet`
 * input — a rejection on either line has nowhere else to land.
 */
const BACKEND_PATH_TO_FORM_FIELD: Record<string, string> = {
  mailingLine1: 'mailingStreet',
  mailingLine2: 'mailingStreet',
  otherLine1: 'otherStreet',
  otherLine2: 'otherStreet',
};

/**
 * Captures both addresses' real `line1`/`line2` shape, alongside the form's seeded display
 * values — see `OriginalAddressLines`'s own comment for why it's needed independent of what
 * `seedProfileFormValues` renders.
 */
function seedOriginalAddressLines(member: MemberProfile): OriginalAddressLines {
  return {
    mailingLine1: member.mailingAddress?.line1 ?? null,
    mailingLine2: member.mailingAddress?.line2 ?? null,
    otherLine1: member.otherAddress?.line1 ?? null,
    otherLine2: member.otherAddress?.line2 ?? null,
  };
}

/** Narrows a Salesforce address's free-text state to the dropdown's closed set — see `isUsStateCode`. */
function seedState(state: string | null): ProfileFormValues['mailingState'] {
  return state && isUsStateCode(state) ? state : '';
}

/** Seeds the form from the read-side `MemberProfile`. */
export function seedProfileFormValues(member: MemberProfile): ProfileFormValues {
  return {
    dateOfBirth: member.dateOfBirth ?? '',
    gender: member.gender ?? '',
    email: member.email ?? '',
    phone: member.phone ?? '',
    mailingStreet: joinAddressLines(member.mailingAddress?.line1 ?? null, member.mailingAddress?.line2 ?? null),
    mailingCity: member.mailingAddress?.city ?? '',
    mailingState: seedState(member.mailingAddress?.state ?? null),
    mailingPostalCode: member.mailingAddress?.zip ?? '',
    salutation: member.salutation && isSalutation(member.salutation) ? member.salutation : '',
    mobilePhone: member.mobilePhone ?? '',
    otherStreet: joinAddressLines(member.otherAddress?.line1 ?? null, member.otherAddress?.line2 ?? null),
    otherCity: member.otherAddress?.city ?? '',
    otherState: seedState(member.otherAddress?.state ?? null),
    otherPostalCode: member.otherAddress?.zip ?? '',
    primaryInsurance: member.primaryInsurance ?? '',
    secondaryInsurance: member.secondaryInsurance ?? '',
    employer: member.employer ?? '',
    jobTitle: member.jobTitle ?? '',
    preferredContactMethod: member.preferredContactMethod ?? '',
  };
}

interface EditProfileDialogContentProps {
  member: MemberProfile;
  onClose: () => void;
  /** Reports save-in-flight state up to the wrapper, which owns the close (X) button. */
  onSavingChange: (isSaving: boolean) => void;
}

function EditProfileDialogContent({ member, onClose, onSavingChange }: EditProfileDialogContentProps) {
  const { t } = useTranslation('members');
  const translateError = useTranslateError();
  // Captured once via the lazy initializer, never recomputed — a `useMemo` keyed on `member` would
  // silently re-seed mid-session if `myMemberInfo` refetches in the background while the dialog is
  // open, shifting the omit/null/value baseline `buildUpdatePayload` diffs against.
  const [initialValues] = useState(() => seedProfileFormValues(member));
  // Same capture-once rationale — the real Salesforce shape behind whichever street fields the
  // member never touches.
  const [originalAddressLines] = useState(() => seedOriginalAddressLines(member));
  // The secondary-address all-or-nothing rule needs `initialValues` to tell "actively editing
  // this group" apart from "already looked like this" — see `createProfileSchema`'s own comment.
  const profileSchema = useMemo(() => createProfileSchema(initialValues), [initialValues]);
  const form = useForm<ProfileFormValues>({ resolver: zodResolver(profileSchema), defaultValues: initialValues });

  const { submit, isPending } = useUpdateMyProfile({
    onSuccess: onClose,
    onValidationError: (issues: ProfileValidationIssue[]) => {
      for (const issue of issues) {
        // Both backend lines of a two-line address collapse onto this form's one flattened field.
        const formPath = BACKEND_PATH_TO_FORM_FIELD[issue.path] ?? issue.path;
        // A path this form doesn't render falls back to the root banner — a rejected save must
        // never go silent with Save just re-enabled.
        const hasField =
          // biome-ignore lint/suspicious/noPrototypeBuiltins: Object.hasOwn needs an ES2022 lib target this tsconfig doesn't have
          Object.prototype.hasOwnProperty.call(initialValues, formPath);
        const path = formPath !== '(root)' && hasField ? (formPath as keyof ProfileFormValues) : 'root';
        form.setError(path, { message: issue.message });
      }
    },
  });
  useEffect(() => onSavingChange(isPending), [isPending, onSavingChange]);

  const handleSave = form.handleSubmit((values) => submit(values, initialValues, originalAddressLines));

  const genderOptions = [
    { value: 'MALE', label: t('profile.genderValues.MALE') },
    { value: 'FEMALE', label: t('profile.genderValues.FEMALE') },
  ];
  // No "None"/clear option: Salesforce's own Contact validation rule treats Salutation as part of
  // a required "Name" completeness group — verified live, clearing it is rejected for any member
  // who already has one on file (i.e. practically everyone). Legacy never offered one either.
  const salutationOptions = [
    { value: 'Mr', label: t('profile.salutationValues.Mr') },
    { value: 'Mrs', label: t('profile.salutationValues.Mrs') },
    { value: 'Miss', label: t('profile.salutationValues.Miss') },
    { value: 'Ms', label: t('profile.salutationValues.Ms') },
    { value: 'Dr', label: t('profile.salutationValues.Dr') },
  ];
  const contactMethodOptions = [
    { value: 'EMAIL', label: t('profile.contactMethodValues.EMAIL') },
    { value: 'MOBILE', label: t('profile.contactMethodValues.MOBILE') },
    { value: 'MAIL', label: t('profile.contactMethodValues.MAIL') },
    { value: 'PHONE', label: t('profile.contactMethodValues.PHONE') },
    { value: 'WORK_PHONE', label: t('profile.contactMethodValues.WORK_PHONE') },
  ];
  // Label is the bare code, not the full state name — matches the handoff, which uses the
  // abbreviation directly as the option text with no separate label at all.
  const stateOptions = US_STATE_CODES.map((code) => ({ value: code, label: code }));

  return (
    <FormProvider {...form}>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('profile.editDialogTitle')}</ResponsiveModalTitle>
        <ResponsiveModalDescription>{t('profile.editDescription')}</ResponsiveModalDescription>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        <div className="space-y-6">
          {form.formState.errors.root?.message && (
            <Alert variant="destructive">
              <Icon name="alertTriangle" weight="solid" />
              <AlertDescription>{translateError(form.formState.errors.root.message)}</AlertDescription>
            </Alert>
          )}

          <div className="rounded-lg border bg-muted p-3.5 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">
              {[member.firstName, member.lastName].filter(Boolean).join(' ') || t('profile.notOnFile')}
            </span>
            <p className="mt-0.5">{t('profile.nameLocked')}</p>
          </div>

          <EditSection title={t('profile.sections.memberInformation')} icon="user">
            <ProfileTextField name="dateOfBirth" label={t('profile.fields.dateOfBirth')} kind="date" />
            <ProfileTextField name="gender" label={t('profile.fields.gender')} kind="select" options={genderOptions} />
            <ProfileTextField
              name="salutation"
              label={t('profile.fields.salutation')}
              kind="select"
              options={salutationOptions}
            />
          </EditSection>

          {/* Phone (primary + mobile), then Email + preferred contact — matches the handoff's order. */}
          <EditSection title={t('profile.sections.contact')} icon="phone">
            <ProfileTextField name="phone" label={t('profile.fields.phone')} inputMode="numeric" format={formatPhone} />
            <ProfileTextField
              name="mobilePhone"
              label={t('profile.fields.mobilePhone')}
              inputMode="numeric"
              format={formatPhone}
            />
            <div className="sm:col-span-2">
              <ProfileTextField name="email" label={t('profile.fields.email')} />
            </div>
            <ProfileTextField
              name="preferredContactMethod"
              label={t('profile.fields.preferredContactMethod')}
              kind="select"
              options={contactMethodOptions}
            />
          </EditSection>

          {/* Primary and Secondary address are two separate groups in the handoff, not one combined
              "Address" section — Secondary is also marked optional there. */}
          <EditSection title={t('profile.fields.mailingAddress')} icon="mapPin">
            <div className="space-y-4 sm:col-span-2">
              <ProfileTextField name="mailingStreet" label={t('profile.fields.street')} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <ProfileTextField name="mailingCity" label={t('profile.fields.city')} />
                <ProfileTextField
                  name="mailingState"
                  label={t('profile.fields.state')}
                  kind="select"
                  options={stateOptions}
                />
                <ProfileTextField
                  name="mailingPostalCode"
                  label={t('profile.fields.zip')}
                  inputMode="numeric"
                  format={formatZip}
                />
              </div>
            </div>
          </EditSection>

          <EditSection title={t('profile.fields.otherAddress')} icon="mapPin" optional>
            <div className="space-y-4 sm:col-span-2">
              <ProfileTextField name="otherStreet" label={t('profile.fields.street')} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <ProfileTextField name="otherCity" label={t('profile.fields.city')} />
                <ProfileTextField
                  name="otherState"
                  label={t('profile.fields.state')}
                  kind="select"
                  options={stateOptions}
                />
                <ProfileTextField
                  name="otherPostalCode"
                  label={t('profile.fields.zip')}
                  inputMode="numeric"
                  format={formatZip}
                />
              </div>
            </div>
          </EditSection>

          {/* Insurance and Employment are two separate groups in the handoff, not one combined
              section — Insurance is also marked optional there. */}
          <EditSection title={t('profile.sections.insurance')} icon="shieldCheck" optional>
            <ProfileTextField name="primaryInsurance" label={t('profile.fields.primaryInsurance')} />
            <ProfileTextField name="secondaryInsurance" label={t('profile.fields.secondaryInsurance')} />
          </EditSection>

          <EditSection title={t('profile.sections.employment')} icon="wallet">
            <ProfileTextField name="employer" label={t('profile.fields.employer')} />
            <ProfileTextField name="jobTitle" label={t('profile.fields.jobTitle')} />
          </EditSection>
        </div>
      </ResponsiveModalBody>

      <ResponsiveModalFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          {t('profile.cancel')}
        </Button>
        <Button type="button" onClick={handleSave} disabled={isPending}>
          {isPending ? (
            <>
              <Icon name="spinner" weight="solid" size="sm" className="animate-spin" /> {t('profile.saving')}
            </>
          ) : (
            t('profile.save')
          )}
        </Button>
      </ResponsiveModalFooter>
    </FormProvider>
  );
}

interface EditSectionProps {
  title: string;
  /** Not every group has one — the handoff only puts an icon on Secondary address and Insurance. */
  icon?: IconName;
  /** Appends an "(optional)" marker to the heading — matches the handoff's Secondary address and
   * Insurance groups, the only two marked optional there. */
  optional?: boolean;
  children: ReactNode;
}

/**
 * One heading + 2-col field grid per handoff group (Contact, Primary/Secondary address, Insurance,
 * Employment, ...) — a finer split than `ProfileDetailsCard`'s 4 read-view sections, matching the
 * handoff's own group structure rather than the read view's.
 */
function EditSection({ title, icon, optional, children }: EditSectionProps) {
  const { t } = useTranslation('members');
  return (
    <div data-slot="profile-edit-section">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        {icon && <Icon name={icon} size="sm" className="text-primary" />}
        {title}
        {optional && <span className="font-normal text-muted-foreground">{t('profile.optionalSuffix')}</span>}
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

// Validation messages are dynamic i18n keys (from profileSchema) or already-display-ready backend
// text (BAD_REQUEST issues) — either way `t()` is safe to call unconditionally: i18next's default
// missing-key behavior is to echo the key back, so an untranslated backend message still displays
// as-is rather than disappearing.
function useTranslateError() {
  const { t } = useTranslation('members');
  return t as unknown as (message: string) => string;
}

/** Members-scoped `FormFloatField`: binds the 'members' i18n namespace and fixes the form-values type. */
function ProfileTextField(props: Omit<FormFloatFieldProps<ProfileFormValues>, 'translateError'>) {
  const translateError = useTranslateError();
  return <FormFloatField<ProfileFormValues> {...props} translateError={translateError} />;
}
